import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckCircle, XCircle, Clock, AlertCircle, Eye, Loader2, Smartphone, Monitor } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../services/api';
import TestRunButton from '../components/TestRunButton';

const TestRuns = () => {
  const [tab, setTab] = useState('web'); // 'web' | 'mobile'
  const [testRuns, setTestRuns] = useState([]);
  const [mobileRuns, setMobileRuns] = useState([]);
  const [mobileTotal, setMobileTotal] = useState(0);
  const [mobilePage, setMobilePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchRuns = () =>
    apiService.getTestRuns().then((res) => {
      setTestRuns(res.data);
      return res.data;
    });

  const fetchMobileRuns = (page = 1) =>
    apiService.getMobileTestRuns({ page, limit: 50, days: 90 }).then((res) => {
      setMobileRuns(res.data.test_runs);
      setMobileTotal(res.data.total);
    });

  useEffect(() => {
    Promise.all([
      fetchRuns(),
      fetchMobileRuns(),
    ])
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  // Refetch mobile when page changes
  useEffect(() => {
    if (!loading) fetchMobileRuns(mobilePage);
  }, [mobilePage]);

  // Auto-poll while any web run is in "running" state
  useEffect(() => {
    const hasRunning = testRuns.some((r) => r.status === 'running');
    if (!hasRunning) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(() => {
      fetchRuns().then((data) => {
        if (!data.some((r) => r.status === 'running')) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      });
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [testRuns.some?.((r) => r.status === 'running')]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Test Runs</h1>
          <p className="text-sm text-gray-500 mt-1">History of all test executions</p>
        </div>
        {tab === 'web' && <TestRunButton />}
      </div>

      {/* Tab toggle */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('web')}
          className={clsx(
            'flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
            tab === 'web' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <Monitor className="h-4 w-4" />
          Web Tests
          <span className="text-xs text-gray-400 ml-1">({testRuns.length})</span>
        </button>
        <button
          onClick={() => setTab('mobile')}
          className={clsx(
            'flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
            tab === 'mobile' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <Smartphone className="h-4 w-4" />
          Mobile Tests
          <span className="text-xs text-gray-400 ml-1">({mobileTotal})</span>
        </button>
      </div>

      {/* Web Test Runs Table */}
      {tab === 'web' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Run</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tests</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Browser</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {testRuns.map((run) => (
                <tr key={run.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-5 py-3 text-sm font-medium text-gray-800">
                    #{run.id}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={run.status} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1 text-sm flex-wrap">
                      <span className="text-success-600 font-medium">{run.passed_tests}</span>
                      <span className="text-gray-300">/</span>
                      <span className="text-gray-600">{run.total_tests}</span>
                      {run.failed_tests > 0 && (
                        <span className="text-error-500 text-xs ml-1">({run.failed_tests} failed)</span>
                      )}
                      {run.cloudflare_blocked_tests > 0 && (
                        <span
                          className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
                          title="Cloudflare bot-protection blocked these tests. Not real failures — the site was unreachable to the runner."
                        >
                          ⚠ Cloudflare blocked {run.cloudflare_blocked_tests}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">
                    {run.duration ? `${(run.duration / 1000).toFixed(1)}s` : '-'}
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {run.browser}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-sm text-gray-500">
                    <div>{format(new Date(run.timestamp), 'MMM dd, yyyy')}</div>
                    <div className="text-xs text-gray-400">{format(new Date(run.timestamp), 'HH:mm')}</div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link
                      to={`/test-runs/${run.id}`}
                      className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {testRuns.length === 0 && (
            <div className="text-center py-16">
              <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No web test runs yet. Run your first test to see results.</p>
            </div>
          )}
        </div>
      )}

      {/* Mobile Test Runs Table */}
      {tab === 'mobile' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Run</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tests</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mobileRuns.map((run) => {
                const passed = run.total_tests - run.total_failures;
                const hasFailed = run.total_failures > 0;
                return (
                  <tr key={run.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3 text-sm font-medium text-gray-800">
                      <div className="flex items-center gap-1.5">
                        <Smartphone className="h-3.5 w-3.5 text-gray-400" />
                        Mobile #{run.id}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={hasFailed ? 'failed' : 'passed'} />
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-success-600 font-medium">{passed}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-gray-600">{run.total_tests}</span>
                        {run.total_failures > 0 && (
                          <span className="text-error-500 text-xs ml-1">({run.total_failures} failed)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">
                      {run.total_time_seconds.toFixed(1)}s
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700">
                        {run.project}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-gray-500">
                      <div>{format(new Date(run.timestamp), 'MMM dd, yyyy')}</div>
                      <div className="text-xs text-gray-400">{format(new Date(run.timestamp), 'HH:mm')}</div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Link
                        to={`/mobile-tests/${run.id}`}
                        className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {mobileRuns.length === 0 && (
            <div className="text-center py-16">
              <Smartphone className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No mobile test runs yet. Configure your CI/CD pipeline to send results.</p>
            </div>
          )}

          {/* Pagination */}
          {mobileTotal > 50 && (
            <div className="flex items-center justify-center gap-3 px-5 py-3 border-t border-gray-100">
              <button
                onClick={() => setMobilePage((p) => Math.max(1, p - 1))}
                disabled={mobilePage <= 1}
                className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Page {mobilePage} of {Math.ceil(mobileTotal / 50)}
              </span>
              <button
                onClick={() => setMobilePage((p) => p + 1)}
                disabled={mobilePage >= Math.ceil(mobileTotal / 50)}
                className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function StatusBadge({ status }) {
  const styles = {
    passed: 'bg-success-50 text-success-700 border-success-200',
    failed: 'bg-error-50 text-error-700 border-error-200',
    partial: 'bg-orange-50 text-orange-700 border-orange-200',
    running: 'bg-primary-50 text-primary-700 border-primary-200',
    cancelled: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-xs font-medium capitalize',
      styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'
    )}>
      {status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
      {status}
    </span>
  );
}

export default TestRuns;
